import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  BookOpenText,
  Loader2,
  Plus,
  Save,
  Trash2,
  Volume2,
} from 'lucide-react';

import { toast } from '../components/CustomToast';
import { useAuth } from '../context/AuthContext';

const apiUrl = process.env.REACT_APP_BACKEND_URL;
const BASE = `${apiUrl}/api/v1/admin`;

const emptyEntry = () => ({
  _key: `${Date.now()}-${Math.random()}`,
  from: '',
  to: '',
  enabled: true,
  note: '',
});

const emptyDraft = (scope, clientId = '', agentId = '') => ({
  scope,
  client_id: scope === 'global' ? null : clientId || null,
  agent_id: scope === 'agent' ? agentId || null : null,
  name: scope === 'global' ? 'Global pronunciation' : '',
  enabled: true,
  domain_lexicon: 'none',
  entries: [],
});

function withKeys(profile) {
  return {
    ...profile,
    entries: (profile.entries || []).map((entry) => ({ ...entry, _key: emptyEntry()._key })),
  };
}

function sentinel(index) {
  return `\uE000${String(index)
    .split('')
    .map((digit) => String.fromCodePoint(0xE010 + Number(digit)))
    .join('')}\uE001`;
}

function dictionaryPreview(text, draft) {
  if (!text || !draft.enabled) return text;
  let output = text;
  const restorations = [];
  const entries = (draft.entries || [])
    .filter((entry) => entry.enabled && entry.from.trim())
    .sort((left, right) => right.from.length - left.from.length);

  entries.forEach((entry) => {
    const escaped = entry.from.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`(?<!\\w)${escaped}(?!\\w)`, 'gi');
    output = output.replace(pattern, () => {
      const index = restorations.length;
      restorations.push(entry.to);
      return sentinel(index);
    });
  });
  restorations.forEach((replacement, index) => {
    output = output.split(sentinel(index)).join(replacement);
  });
  return output;
}

function FieldLabel({ children }) {
  return <label className="mb-1.5 block text-sm font-medium text-slate-700">{children}</label>;
}

export default function PronunciationDashboard() {
  const { token } = useAuth();
  const auth = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token],
  );

  const [profiles, setProfiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [voices, setVoices] = useState([]);
  const [scope, setScope] = useState('global');
  const [clientId, setClientId] = useState('');
  const [agentId, setAgentId] = useState('');
  const [profileId, setProfileId] = useState(null);
  const [draft, setDraft] = useState(() => emptyDraft('global'));
  const [effective, setEffective] = useState(null);
  const [previewInput, setPreviewInput] = useState('');
  const [selectedVoiceId, setSelectedVoiceId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hearing, setHearing] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);

  const previewOutput = useMemo(
    () => dictionaryPreview(previewInput, draft),
    [previewInput, draft],
  );

  const fetchProfiles = useCallback(async () => {
    const { data } = await axios.get(`${BASE}/pronunciations`, auth);
    setProfiles(data || []);
    return data || [];
  }, [auth]);

  const fetchEffective = useCallback(async (selectedAgentId) => {
    if (!selectedAgentId) {
      setEffective(null);
      return;
    }
    try {
      const { data } = await axios.get(
        `${BASE}/pronunciations/effective?agent_id=${encodeURIComponent(selectedAgentId)}`,
        auth,
      );
      setEffective(data);
    } catch (_err) {
      setEffective(null);
    }
  }, [auth]);

  useEffect(() => {
    if (!token) return;
    let active = true;
    Promise.all([
      fetchProfiles(),
      axios.get(`${BASE}/users`, auth),
      axios.get(`${BASE}/instant-clone/voices`, auth),
    ])
      .then(([, usersResponse, voicesResponse]) => {
        if (!active) return;
        setUsers(usersResponse.data?.users || []);
        const loadedVoices = voicesResponse.data?.voices || [];
        setVoices(loadedVoices);
        setSelectedVoiceId((current) => current || loadedVoices[0]?.id || '');
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail || 'Failed to load pronunciation settings');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [auth, fetchProfiles, token]);

  useEffect(() => {
    if (!clientId || scope === 'global') {
      setAgents([]);
      if (scope !== 'agent') setAgentId('');
      return;
    }
    let active = true;
    axios.get(
      `${BASE}/users/${encodeURIComponent(clientId)}/agents`,
      auth,
    )
      .then(({ data }) => {
        if (active) setAgents(data?.agents || []);
      })
      .catch(() => {
        if (active) setAgents([]);
        toast.error('Failed to load agents for this client');
      });
    return () => {
      active = false;
    };
  }, [auth, clientId, scope]);

  useEffect(() => {
    const match = profiles.find((profile) => (
      profile.scope === scope
      && (scope === 'global' || profile.client_id === clientId)
      && (scope !== 'agent' || profile.agent_id === agentId)
    ));
    if (match) {
      setProfileId(match.id);
      setDraft(withKeys(match));
      return;
    }
    const selectedUser = users.find((user) => user.id === clientId);
    const selectedAgent = agents.find((agent) => agent.agent_id === agentId);
    const next = emptyDraft(scope, clientId, agentId);
    if (scope === 'client' && selectedUser) {
      next.name = `${selectedUser.name || selectedUser.email || 'Client'} pronunciation`;
    }
    if (scope === 'agent' && selectedAgent) {
      next.name = `${selectedAgent.agent_name || selectedAgent.agent_id} pronunciation`;
    }
    setProfileId(null);
    setDraft(next);
  }, [agentId, agents, clientId, profiles, scope, users]);

  useEffect(() => {
    if (scope === 'agent' && agentId) fetchEffective(agentId);
    else setEffective(null);
  }, [agentId, fetchEffective, profiles, scope]);

  useEffect(() => () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
  }, [audioUrl]);

  const updateDraft = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const updateEntry = (key, field, value) => {
    setDraft((current) => ({
      ...current,
      entries: current.entries.map((entry) => (
        entry._key === key ? { ...entry, [field]: value } : entry
      )),
    }));
  };

  const save = async () => {
    if ((scope === 'client' || scope === 'agent') && !clientId) {
      toast.error('Select a client first');
      return;
    }
    if (scope === 'agent' && !agentId) {
      toast.error('Select an agent first');
      return;
    }
    if (!draft.name.trim()) {
      toast.error('Profile name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        id: profileId,
        ...draft,
        scope,
        client_id: scope === 'global' ? null : clientId,
        agent_id: scope === 'agent' ? agentId : null,
        name: draft.name.trim(),
        // Blank draft rows are never sent: the API 422s on an empty `from`
        // (and an unsaved empty row carries no meaning). Disabled-but-filled
        // rows are kept -- the backend stores them and skips them at merge.
        entries: draft.entries
          .filter((entry) => entry.from.trim() !== '')
          .map(({ _key, ...entry }) => entry),
      };
      await axios.put(`${BASE}/pronunciations`, payload, auth);
      await fetchProfiles();
      if (scope === 'agent') await fetchEffective(agentId);
      toast.success('Pronunciation profile saved. Workers pick it up within 30 seconds.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!profileId || !window.confirm('Delete this pronunciation profile?')) return;
    try {
      await axios.delete(`${BASE}/pronunciations/${profileId}`, auth);
      await fetchProfiles();
      setProfileId(null);
      toast.success('Pronunciation profile deleted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete profile');
    }
  };

  const hearPreview = async () => {
    if (!selectedVoiceId || !previewOutput.trim()) return;
    setHearing(true);
    try {
      const response = await axios.post(
        `${BASE}/instant-clone/voices/${selectedVoiceId}/test`,
        { text: previewOutput },
        { ...auth, responseType: 'blob' },
      );
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      const nextUrl = URL.createObjectURL(response.data);
      setAudioUrl(nextUrl);
      await new Audio(nextUrl).play();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not synthesize the preview');
    } finally {
      setHearing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center text-slate-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading pronunciation profiles...
      </div>
    );
  }

  return (
    <div className="flex flex-col px-8 pb-10 pt-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mb-1 flex items-center gap-2 text-xs font-medium text-violet-600">
            <BookOpenText size={14} /> OmniVoice
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Pronunciation profiles</h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">
            Layer dictionaries from global to client to agent. More specific entries override earlier layers.
          </p>
        </div>
        <div className="flex gap-2">
          {profileId && (
            <button
              type="button"
              onClick={remove}
              className="inline-flex items-center gap-2 rounded-lg border border-rose-200 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50"
            >
              <Trash2 size={15} /> Delete
            </button>
          )}
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
            Save profile
          </button>
        </div>
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1.5fr)_minmax(320px,1fr)]">
        <div className="space-y-6">
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="grid gap-5 md:grid-cols-2">
              <div className="md:col-span-2">
                <FieldLabel>Scope</FieldLabel>
                <div className="inline-flex rounded-xl bg-slate-100 p-1">
                  {['global', 'client', 'agent'].map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setScope(value)}
                      className={`rounded-lg px-5 py-2 text-sm font-semibold capitalize ${
                        scope === value
                          ? 'bg-white text-violet-700 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      {value}
                    </button>
                  ))}
                </div>
              </div>

              {scope !== 'global' && (
                <div>
                  <FieldLabel>Client</FieldLabel>
                  <select
                    value={clientId}
                    onChange={(event) => {
                      setClientId(event.target.value);
                      setAgentId('');
                    }}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                  >
                    <option value="">Select client...</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name || user.email || user.id}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {scope === 'agent' && (
                <div>
                  <FieldLabel>Agent</FieldLabel>
                  <select
                    value={agentId}
                    onChange={(event) => setAgentId(event.target.value)}
                    disabled={!clientId}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm disabled:bg-slate-100"
                  >
                    <option value="">Select agent...</option>
                    {agents.map((agent) => (
                      <option key={agent.agent_id} value={agent.agent_id}>
                        {agent.agent_name || agent.agent_id}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <FieldLabel>Profile name</FieldLabel>
                <input
                  value={draft.name}
                  onChange={(event) => updateDraft('name', event.target.value)}
                  maxLength={120}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Hero MotoCorp"
                />
              </div>

              <div>
                <FieldLabel>Domain lexicon</FieldLabel>
                <select
                  value={draft.domain_lexicon}
                  onChange={(event) => updateDraft('domain_lexicon', event.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="none">None</option>
                  <option value="hero">Hero MotoCorp</option>
                </select>
              </div>

              <label className="flex items-center gap-3 text-sm font-medium text-slate-700 md:col-span-2">
                <input
                  type="checkbox"
                  checked={draft.enabled}
                  onChange={(event) => updateDraft('enabled', event.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-violet-600"
                />
                Profile enabled
              </label>
            </div>
          </section>

          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <h2 className="font-semibold text-slate-900">Dictionary entries</h2>
                <p className="mt-0.5 text-xs text-slate-500">Whole word or phrase, case-insensitive. Longest match wins.</p>
              </div>
              <button
                type="button"
                onClick={() => updateDraft('entries', [...draft.entries, emptyEntry()])}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <Plus size={15} /> Add row
              </button>
            </div>
            {draft.entries.length === 0 ? (
              <div className="px-6 py-12 text-center text-sm text-slate-400">No dictionary entries yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="w-16 px-4 py-3">On</th>
                      <th className="px-4 py-3">From</th>
                      <th className="px-4 py-3">To</th>
                      <th className="px-4 py-3">Note</th>
                      <th className="w-14 px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {draft.entries.map((entry) => (
                      <tr key={entry._key}>
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={entry.enabled}
                            onChange={(event) => updateEntry(entry._key, 'enabled', event.target.checked)}
                            className="h-4 w-4 rounded border-slate-300 text-violet-600"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            value={entry.from}
                            onChange={(event) => updateEntry(entry._key, 'from', event.target.value)}
                            maxLength={200}
                            className="w-full rounded-lg border border-slate-300 px-3 py-2"
                            placeholder="namaste"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            value={entry.to}
                            onChange={(event) => updateEntry(entry._key, 'to', event.target.value)}
                            maxLength={500}
                            className="w-full rounded-lg border border-slate-300 px-3 py-2"
                            placeholder="nuhmusthe"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            value={entry.note || ''}
                            onChange={(event) => updateEntry(entry._key, 'note', event.target.value)}
                            maxLength={500}
                            className="w-full rounded-lg border border-slate-300 px-3 py-2"
                            placeholder="Optional"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => updateDraft(
                              'entries',
                              draft.entries.filter((row) => row._key !== entry._key),
                            )}
                            className="text-slate-400 hover:text-rose-600"
                            title="Delete row"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        <div className="space-y-6">
          {scope === 'agent' && effective && (
            <section className="rounded-2xl border border-violet-200 bg-violet-50 p-5">
              <h2 className="text-sm font-semibold text-violet-900">Currently effective</h2>
              <p className="mt-2 text-sm text-violet-800">
                {effective.entries.length} entries · {effective.domain_lexicon === 'hero' ? 'Hero lexicon' : 'No domain lexicon'}
              </p>
              <p className="mt-1 text-xs text-violet-600">
                Layers: {effective.layers.length ? effective.layers.join(' → ') : 'none'}
              </p>
            </section>
          )}

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="font-semibold text-slate-900">Preview</h2>
            <p className="mt-1 text-xs text-slate-500">
              Authoring aid for this draft dictionary only. Number, acronym, and domain rules are not shown.
            </p>
            <textarea
              value={previewInput}
              onChange={(event) => setPreviewInput(event.target.value)}
              rows={4}
              className="mt-4 w-full resize-y rounded-lg border border-slate-300 px-3 py-2 text-sm"
              placeholder="Type a sentence containing one of the phrases..."
            />
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Dictionary output</div>
              <div className="mt-2 min-h-12 whitespace-pre-wrap text-sm text-slate-800">
                {previewOutput || <span className="text-slate-400">Preview appears here.</span>}
              </div>
            </div>

            <div className="mt-4">
              <FieldLabel>Preview voice</FieldLabel>
              <select
                value={selectedVoiceId}
                onChange={(event) => setSelectedVoiceId(event.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="">Select an OmniVoice clone...</option>
                {voices.map((voice) => (
                  <option key={voice.id} value={voice.id}>{voice.voice_id}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={hearPreview}
              disabled={hearing || !selectedVoiceId || !previewOutput.trim()}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-40"
            >
              {hearing ? <Loader2 size={16} className="animate-spin" /> : <Volume2 size={16} />}
              Hear it
            </button>
          </section>
        </div>
      </div>
    </div>
  );
}
